import type {
	CaptureChunk,
	CaptureFailure,
	CaptureInterruption,
	CapturePermission,
	CapturePort,
	CaptureSnapshot,
	CaptureState,
} from '@Domain/Ports/Capture/CapturePort';
import wrapNativeAudioContext, {
	type CaptureAnalyser,
	type CaptureAudioContext,
} from '@Infra/Capture/wrapNativeAudioContext';
import wrapNativeRecorder, { type CaptureRecorder } from '@Infra/Capture/wrapNativeRecorder';

/** The slice of `MediaDevices` this adapter needs: nothing else is ever read from it. */
type MicrophoneSource = Pick<MediaDevices, 'getUserMedia'>;

/** Builds a `CaptureRecorder` for a given stream and negotiated MIME type. */
type MediaRecorderFactory = (stream: MediaStream, mimeType: string) => CaptureRecorder;

type AudioContextFactory = () => CaptureAudioContext;

/** The slice of `Document` this adapter needs to detect a return to the foreground. */
type VisibilityEventTarget = {
	addEventListener(type: 'visibilitychange', listener: () => void): void;
	removeEventListener(type: 'visibilitychange', listener: () => void): void;
	readonly hidden: boolean;
};

type StreamAcquisition = { readonly stream: MediaStream } | { readonly failure: CaptureFailure };

/** Ordered by preference: the first MIME type `isTypeSupported` accepts wins the negotiation. */
const MIME_TYPE_CANDIDATES: readonly string[] = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

const ACTIVE_STATES: readonly CaptureState[] = ['recording', 'paused', 'interrupted'];

const defaultMediaRecorderFactory: MediaRecorderFactory = (stream, mimeType) =>
	wrapNativeRecorder(new MediaRecorder(stream, { mimeType }));

const defaultIsTypeSupported = (mimeType: string): boolean => MediaRecorder.isTypeSupported(mimeType);

const defaultAudioContextFactory: AudioContextFactory = () => wrapNativeAudioContext(new AudioContext());

/**
 * Drives microphone capture through `MediaRecorder`, segmenting it around whatever interrupts it.
 *
 * Every browser surface it touches is a constructor parameter, defaulted from `navigator` /
 * `globalThis` at construction time rather than read from the module scope: jsdom implements none
 * of `MediaDevices`, `MediaRecorder` or `AudioPlayerContext`, so a test supplies its own fakes for the
 * factories and the microphone source instead of a global polyfill.
 *
 * A restart of the recorder — after a recoverable interruption, or a voluntary `rotateSegment()` —
 * always opens a fresh segment: `MediaRecorder` writes a brand-new container header on every
 * `start()`, and concatenating fragments across two segments would produce an unreadable file.
 */
class WebCaptureAdapter implements CapturePort {
	private state: CaptureState = 'idle';
	private mimeType: string | null = null;
	private segment = 0;
	private interruptionCount = 0;
	private lastInterruptionAt: number | null = null;
	private startedAtMs: number | null = null;
	private pausedAtMs: number | null = null;
	private pausedTotalMs = 0;
	private timesliceMs = 0;
	private wasHidden = false;
	private visibilitySubscribed = false;

	private stream: MediaStream | null = null;
	private recorder: CaptureRecorder | null = null;
	private audioContext: CaptureAudioContext | null = null;
	private analyser: CaptureAnalyser | null = null;

	private readonly snapshotListeners = new Set<(snapshot: CaptureSnapshot) => void>();
	private readonly chunkListeners = new Set<(chunk: CaptureChunk) => void>();
	private readonly interruptionListeners = new Set<(interruption: CaptureInterruption) => void>();
	private readonly failureListeners = new Set<(failure: CaptureFailure) => void>();

	constructor(
		private readonly mediaDevices: MicrophoneSource = navigator.mediaDevices,
		private readonly createRecorder: MediaRecorderFactory = defaultMediaRecorderFactory,
		private readonly isTypeSupported: (mimeType: string) => boolean = defaultIsTypeSupported,
		private readonly createAudioContext: AudioContextFactory = defaultAudioContextFactory,
		private readonly visibilityTarget: VisibilityEventTarget = document,
		private readonly clock: () => number = () => Date.now(),
	) {}

	async queryPermission(): Promise<CapturePermission> {
		if (typeof this.mediaDevices.getUserMedia !== 'function') {
			return 'unsupported';
		}
		// No Permissions API surface is injected, and probing further would mean calling
		// getUserMedia itself — a side-effecting prompt, not a plain query. 'granted'/'denied' are
		// only known once start() is actually attempted.
		return 'prompt';
	}

	async start(options: { readonly timesliceMs: number }): Promise<void> {
		if (ACTIVE_STATES.includes(this.state)) {
			return;
		}

		this.subscribeToVisibility();
		this.timesliceMs = options.timesliceMs;
		this.segment = 0;
		this.interruptionCount = 0;
		this.lastInterruptionAt = null;
		this.wasHidden = false;
		this.startedAtMs = this.clock();
		this.pausedAtMs = null;
		this.pausedTotalMs = 0;

		const mimeType = this.negotiateMimeType();
		if (mimeType === null) {
			this.failStart({ reason: 'unsupported' });
			return;
		}
		this.mimeType = mimeType;

		const acquisition = await this.acquireStream();
		if ('failure' in acquisition) {
			this.failStart(acquisition.failure);
			return;
		}

		this.beginSegment(acquisition.stream);
	}

	pause(): void {
		if (this.state !== 'recording' || this.recorder === null) {
			return;
		}
		this.recorder.pause();
		// Keep the first pause instant: an automatic resume after an interruption re-pauses the recorder
		// while the user is still paused.
		this.pausedAtMs ??= this.clock();
		this.state = 'paused';
		this.notifySnapshotChange();
	}

	resume(): void {
		if (this.state !== 'paused' || this.recorder === null) {
			return;
		}
		this.recorder.resume();
		if (this.pausedAtMs !== null) {
			this.pausedTotalMs += this.clock() - this.pausedAtMs;
			this.pausedAtMs = null;
		}
		this.state = 'recording';
		this.notifySnapshotChange();
	}

	rotateSegment(): void {
		if (!ACTIVE_STATES.includes(this.state) || this.stream === null) {
			return;
		}
		const stream = this.stream;
		this.teardownRecorder();
		this.segment += 1;
		this.beginSegment(stream);
	}

	async stop(): Promise<void> {
		const recorder = this.recorder;
		if (recorder !== null) {
			await new Promise<void>((resolve) => {
				recorder.onStop(() => resolve());
				recorder.onError(() => resolve());
				if (recorder.state === 'inactive') {
					resolve();
					return;
				}
				try {
					recorder.stop();
				} catch {
					resolve();
				}
			});
		}
		this.recorder = null;

		if (this.audioContext !== null) {
			await this.audioContext.close();
			this.audioContext = null;
			this.analyser = null;
		}

		this.releaseStream();
		this.unsubscribeFromVisibility();
		this.state = 'stopped';
		this.notifySnapshotChange();
	}

	snapshot(): CaptureSnapshot {
		const now = this.clock();
		return Object.freeze({
			state: this.state,
			elapsedMs: this.startedAtMs === null ? 0 : now - this.startedAtMs,
			recordedMs:
				this.startedAtMs === null
					? 0
					: Math.max(0, (this.pausedAtMs ?? now) - this.startedAtMs - this.pausedTotalMs),
			level: this.readLevel(),
			mimeType: this.mimeType,
			segment: this.segment,
			interruptionCount: this.interruptionCount,
			lastInterruptionAt: this.lastInterruptionAt,
		});
	}

	subscribe(listener: (snapshot: CaptureSnapshot) => void): () => void {
		this.snapshotListeners.add(listener);
		return () => {
			this.snapshotListeners.delete(listener);
		};
	}

	onChunk(handler: (chunk: CaptureChunk) => void): () => void {
		this.chunkListeners.add(handler);
		return () => {
			this.chunkListeners.delete(handler);
		};
	}

	onInterruption(handler: (interruption: CaptureInterruption) => void): () => void {
		this.interruptionListeners.add(handler);
		return () => {
			this.interruptionListeners.delete(handler);
		};
	}

	onFailure(handler: (failure: CaptureFailure) => void): () => void {
		this.failureListeners.add(handler);
		return () => {
			this.failureListeners.delete(handler);
		};
	}

	private negotiateMimeType(): string | null {
		return MIME_TYPE_CANDIDATES.find((candidate) => this.isTypeSupported(candidate)) ?? null;
	}

	private async acquireStream(): Promise<StreamAcquisition> {
		if (typeof this.mediaDevices.getUserMedia !== 'function') {
			return { failure: { reason: 'no-microphone' } };
		}
		try {
			const stream = await this.mediaDevices.getUserMedia({ audio: true });
			return { stream };
		} catch (error) {
			return { failure: this.mapErrorToFailure(error) };
		}
	}

	private mapErrorToFailure(error: unknown): CaptureFailure {
		const name = error instanceof Error ? error.name : null;
		if (name === 'NotAllowedError' || name === 'SecurityError') {
			return { reason: 'permission-denied' };
		}
		if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
			return { reason: 'no-microphone' };
		}
		const detail = error instanceof Error ? error.message : 'Erreur inconnue lors de la capture audio.';
		return { reason: 'unrecoverable', detail };
	}

	/** Starts recording on a freshly acquired stream, tagging every fragment with the segment
	 * number and a fragment index that live in this call's own closure — never on shared mutable
	 * state — so a stray final flush from a just-discarded recorder can never be mislabeled into
	 * the segment that replaces it. */
	private beginSegment(stream: MediaStream): void {
		const mimeType = this.mimeType;
		if (mimeType === null) {
			return;
		}

		this.stream = stream;
		for (const track of stream.getTracks()) {
			track.onended = () => this.recoverFromInterruption('track-ended');
			track.onmute = () => this.recoverFromInterruption('track-muted');
		}

		const segmentNumber = this.segment;
		let fragmentIndex = 0;

		const recorder = this.createRecorder(stream, mimeType);
		recorder.onDataAvailable((blob) => {
			if (blob.size === 0) {
				return;
			}
			const chunk: CaptureChunk = Object.freeze({
				segment: segmentNumber,
				index: fragmentIndex,
				blob,
				atMs: this.clock(),
			});
			fragmentIndex += 1;
			this.chunkListeners.forEach((listener) => {
				listener(chunk);
			});
		});
		recorder.onError(() => this.recoverFromInterruption('recorder-error'));
		recorder.onStop(() => {});

		this.recorder = recorder;
		this.audioContext = this.createAudioContext();
		this.analyser = this.audioContext.connectStream(stream);
		recorder.start(this.timesliceMs);
		this.state = 'recording';
		this.notifySnapshotChange();
	}

	private recoverFromInterruption(cause: CaptureInterruption['cause']): void {
		if (!ACTIVE_STATES.includes(this.state)) {
			return;
		}
		void this.performRecovery(cause);
	}

	private async performRecovery(cause: CaptureInterruption['cause']): Promise<void> {
		const at = this.clock();
		this.interruptionCount += 1;
		this.lastInterruptionAt = at;
		this.state = 'interrupted';
		this.notifyInterruption({ cause, at, recovered: true });
		this.notifySnapshotChange();

		this.teardownRecorder();
		this.releaseStream();

		const acquisition = await this.acquireStream();
		if ('failure' in acquisition) {
			this.failActive(acquisition.failure);
			return;
		}

		this.segment += 1;
		this.beginSegment(acquisition.stream);
	}

	private readonly handleVisibilityChange = (): void => {
		if (this.visibilityTarget.hidden) {
			this.wasHidden = true;
			return;
		}
		if (!this.wasHidden) {
			return;
		}
		this.wasHidden = false;
		this.recoverFromInterruption('backgrounded');
	};

	private teardownRecorder(): void {
		if (this.recorder !== null) {
			const recorder = this.recorder;
			recorder.onStop(() => {});
			recorder.onError(() => {});
			if (recorder.state !== 'inactive') {
				try {
					recorder.stop();
				} catch {
					// A recorder already broken by the error that triggered this recovery can refuse a
					// further stop() call; there is nothing left to flush from it.
				}
			}
			this.recorder = null;
		}
		if (this.audioContext !== null) {
			void this.audioContext.close();
			this.audioContext = null;
			this.analyser = null;
		}
	}

	private releaseStream(): void {
		if (this.stream === null) {
			return;
		}
		for (const track of this.stream.getTracks()) {
			track.onended = null;
			track.onmute = null;
			track.stop();
		}
		this.stream = null;
	}

	private readLevel(): number {
		if (this.analyser === null || this.analyser.fftSize <= 0) {
			return 0;
		}
		const buffer = new Uint8Array(this.analyser.fftSize);
		this.analyser.getByteTimeDomainData(buffer);
		let sumOfSquaredDeviations = 0;
		for (const sample of buffer) {
			const deviation = sample - 128;
			sumOfSquaredDeviations += deviation * deviation;
		}
		const rootMeanSquare = Math.sqrt(sumOfSquaredDeviations / buffer.length);
		return Math.min(1, rootMeanSquare / 128);
	}

	private failStart(failure: CaptureFailure): void {
		this.mimeType = null;
		this.unsubscribeFromVisibility();
		this.state = 'stopped';
		this.notifyFailure(failure);
		this.notifySnapshotChange();
	}

	private failActive(failure: CaptureFailure): void {
		this.unsubscribeFromVisibility();
		this.state = 'stopped';
		this.notifyFailure(failure);
		this.notifySnapshotChange();
	}

	/** Visibility only matters while a capture is actually running: subscribing outside `start()`
	 * would fire `handleVisibilityChange` for a session that has nothing to recover. Both methods
	 * are idempotent on `visibilitySubscribed`, so a repeated `start()`/`stop()` pair never
	 * double-subscribes and never calls `removeEventListener` without a matching subscription. */
	private subscribeToVisibility(): void {
		if (this.visibilitySubscribed) {
			return;
		}
		this.visibilityTarget.addEventListener('visibilitychange', this.handleVisibilityChange);
		this.visibilitySubscribed = true;
	}

	private unsubscribeFromVisibility(): void {
		if (!this.visibilitySubscribed) {
			return;
		}
		this.visibilityTarget.removeEventListener('visibilitychange', this.handleVisibilityChange);
		this.visibilitySubscribed = false;
	}

	private notifySnapshotChange(): void {
		const snapshot = this.snapshot();
		this.snapshotListeners.forEach((listener) => {
			listener(snapshot);
		});
	}

	private notifyInterruption(interruption: CaptureInterruption): void {
		this.interruptionListeners.forEach((listener) => {
			listener(interruption);
		});
	}

	private notifyFailure(failure: CaptureFailure): void {
		this.failureListeners.forEach((listener) => {
			listener(failure);
		});
	}
}

export default WebCaptureAdapter;
export type { MicrophoneSource, MediaRecorderFactory, AudioContextFactory, VisibilityEventTarget };
